
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ChevronRight, AlertCircle, AlertTriangle, CalendarClock, Bell, CreditCard, RotateCcw, Check } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { fmt, MONTH_OPTIONS } from './constants';
import { RevenueConcentrationCard } from '@/components/RevenueConcentrationCard';

const round2 = v => Math.round(v * 100) / 100;

export function ReportsTab() {
  const [invoices, setInvoices] = useState([]);
  const [aging, setAging] = useState(null);
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reportMonth, setReportMonth] = useState('all');
  const [agingFilter, setAgingFilter] = useState(null);
  const [revenueDetailMonth, setRevenueDetailMonth] = useState(null);
  const [cashDetailMonth, setCashDetailMonth] = useState(null);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [apAging, setApAging] = useState(null);
  const [apAgingFilter, setApAgingFilter] = useState(null);
  const [flagsOpen, setFlagsOpen] = useState(false);
  const [dismissedEmps, setDismissedEmps] = useState(new Set());
  const [dismissedProjs, setDismissedProjs] = useState(new Set());

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/invoices'),
      api.get('/accounting/invoice-aging'),
      api.get('/accounting/delinquency-flags'),
      api.get('/accounts-payable/aging'),
    ]).then(([iRes, aRes, fRes, apRes]) => {
      setInvoices(iRes.data);
      setAging(aRes.data);
      setFlags(fRes.data);
      setApAging(apRes.data);
      // Restore dismissed state from backend
      setDismissedEmps(new Set(fRes.data?.dismissed_employee_ids || []));
      setDismissedProjs(new Set(fRes.data?.dismissed_project_ids || []));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="animate-pulse text-muted-foreground p-4">Loading...</div>;

  // Filter invoices by billable month if set
  const filteredInvoices = reportMonth && reportMonth !== 'all'
    ? invoices.filter(i => {
        const invMonth = i.billable_month || (i.sent_at || i.created_at || '').slice(0, 7);
        return invMonth === reportMonth;
      })
    : invoices;

  // === Table 1: Monthly Billing Summary (grouped by billable month) ===
  const excludeStatuses = includeCancelled ? ['Draft'] : ['Draft', 'Cancelled'];
  const billingByMonth = {};
  filteredInvoices.filter(i => !excludeStatuses.includes(i.status)).forEach(inv => {
    const month = inv.billable_month || (inv.sent_at || inv.created_at || '').slice(0, 7);
    if (!billingByMonth[month]) billingByMonth[month] = { invoiced: 0, collected: 0, outstanding: 0, count: 0, invoices: [] };
    const d = billingByMonth[month];
    d.invoiced += inv.total || 0;
    const paid = inv.amount_paid || 0;
    d.collected += paid;
    d.outstanding += round2((inv.total || 0) - paid);
    d.count += 1;
    d.invoices.push(inv);
  });
  const billingMonths = Object.keys(billingByMonth).sort().reverse();

  // === Table 2: Monthly Cash Receipts (grouped by payment date) ===
  const cashByMonth = {};
  filteredInvoices.filter(i => !excludeStatuses.includes(i.status)).forEach(inv => {
    (inv.payments || []).forEach(p => {
      const payMonth = (p.date || '').slice(0, 7);
      if (!payMonth) return;
      if (!cashByMonth[payMonth]) cashByMonth[payMonth] = { received: 0, paymentCount: 0, payments: [] };
      cashByMonth[payMonth].received = round2(cashByMonth[payMonth].received + (p.amount || 0));
      cashByMonth[payMonth].paymentCount += 1;
      cashByMonth[payMonth].payments.push({
        ...p,
        invoice_number: inv.invoice_number,
        invoice_id: inv.id,
        client_name: inv.client_name || inv.project_name || '-',
      });
    });
  });
  const cashMonths = Object.keys(cashByMonth).sort().reverse();

  const agingBuckets = [
    { label: 'Current (0-30)', key: 'current', color: 'text-emerald-400' },
    { label: '31-60 Days', key: '30', color: 'text-amber-400' },
    { label: '61-90 Days', key: '60', color: 'text-orange-400' },
    { label: '90+ Days', key: '90_plus', color: 'text-rose-400' },
  ];

  const empFlags = flags?.employee_flags || [];
  const projFlags = flags?.project_flags || [];
  const activeEmpFlags = empFlags.filter(f => !dismissedEmps.has(f.employee_id));
  const activeProjFlags = projFlags.filter(f => !dismissedProjs.has(f.project_id));
  const totalActiveFlags = activeEmpFlags.length + activeProjFlags.length;
  const totalFlags = empFlags.length + projFlags.length;

  const dismissFlag = async (entityType, entityId) => {
    try {
      await api.post('/accounting/delinquency-dismiss', { entity_type: entityType, entity_id: entityId });
      if (entityType === 'employee') setDismissedEmps(prev => new Set([...prev, entityId]));
      else setDismissedProjs(prev => new Set([...prev, entityId]));
    } catch { toast.error('Failed to dismiss flag'); }
  };

  const restoreFlag = async (entityType, entityId) => {
    try {
      await api.post('/accounting/delinquency-restore', { entity_type: entityType, entity_id: entityId });
      if (entityType === 'employee') setDismissedEmps(prev => { const s = new Set(prev); s.delete(entityId); return s; });
      else setDismissedProjs(prev => { const s = new Set(prev); s.delete(entityId); return s; });
    } catch { toast.error('Failed to restore flag'); }
  };

  const sendAlerts = async () => {
    setSending(true);
    try {
      const r = await api.post('/accounting/send-delinquency-alerts', {
        exclude_employee_ids: [...dismissedEmps],
        exclude_project_ids: [...dismissedProjs],
      });
      const count = r.data?.alerts_sent || 0;
      if (count > 0) {
        toast.success(`${count} alert notification${count > 1 ? 's' : ''} sent`);
        // Refresh flags to update already_alerted status
        api.get('/accounting/delinquency-flags').then(r => {
          setFlags(r.data);
          setDismissedEmps(new Set(r.data?.dismissed_employee_ids || []));
          setDismissedProjs(new Set(r.data?.dismissed_project_ids || []));
        }).catch(() => {});
      } else {
        toast.info("No new alerts to send (already notified this period)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send alerts");
    }
    setSending(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg" data-testid="reports-heading">Reports & Alerts</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="include-cancelled-toggle">
            <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)}
              className="rounded border-border accent-primary h-3.5 w-3.5" />
            Include cancelled
          </label>
          <Select value={reportMonth} onValueChange={v => { setReportMonth(v); setAgingFilter(null); setRevenueDetailMonth(null); setCashDetailMonth(null); }}>
            <SelectTrigger className="w-48 h-9 bg-card border-border text-xs" data-testid="reports-month-filter">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {MONTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Delinquency Flags Button */}
      {totalFlags > 0 && (
        <Button
          variant="outline"
          size="sm"
          className={`text-xs gap-1.5 ${totalActiveFlags > 0 ? 'border-amber-500/40 text-amber-400 hover:bg-amber-500/10' : 'border-border text-muted-foreground'}`}
          onClick={() => setFlagsOpen(true)}
          data-testid="open-delinquency-flags-btn"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Delinquency Flags
          {totalActiveFlags > 0 && (
            <span className="ml-0.5 h-5 min-w-[20px] px-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center">{totalActiveFlags}</span>
          )}
        </Button>
      )}

      {/* Delinquency Flags Dialog */}
      <Dialog open={flagsOpen} onOpenChange={setFlagsOpen}>
        <DialogContent className="sm:max-w-2xl bg-card border-border max-h-[80vh] overflow-y-auto" data-testid="delinquency-flags-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-400" /> Delinquency Flags
            </DialogTitle>
            <DialogDescription className="text-xs">
              {totalActiveFlags > 0
                ? `${totalActiveFlags} active flag${totalActiveFlags !== 1 ? 's' : ''} requiring attention. Dismissed flags won't receive alert notifications.`
                : 'All flags have been dismissed for this period.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Action bar */}
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {totalActiveFlags} active · {dismissedEmps.size + dismissedProjs.size} dismissed
              </p>
              {totalActiveFlags > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  onClick={sendAlerts}
                  disabled={sending}
                  data-testid="send-delinquency-alerts-btn"
                >
                  <Bell className="h-3 w-3 mr-1" /> {sending ? 'Sending...' : 'Send Alerts for Active Flags'}
                </Button>
              )}
            </div>

            {/* Employee flags */}
            {empFlags.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Employees &mdash; 0 Hours Past Wednesday</p>
                <div className="space-y-1">
                  {empFlags.map(f => {
                    const isDismissed = dismissedEmps.has(f.employee_id);
                    return (
                      <div key={f.employee_id} className={`flex items-center justify-between py-2 px-3 rounded-md border ${isDismissed ? 'border-border/50 bg-secondary/10 opacity-60' : 'border-amber-500/20 bg-amber-500/5'}`} data-testid={`flag-emp-${f.employee_id}`}>
                        <div className="flex items-center gap-2">
                          <AlertCircle className={`h-3.5 w-3.5 ${isDismissed ? 'text-muted-foreground' : 'text-amber-400'}`} />
                          <span className={`text-sm font-medium ${isDismissed ? 'line-through text-muted-foreground' : ''}`}>{f.employee_name}</span>
                          {f.already_alerted && <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-500/30"><Check className="h-2.5 w-2.5 mr-0.5" />Alerted</Badge>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => isDismissed ? restoreFlag('employee', f.employee_id) : dismissFlag('employee', f.employee_id)}
                          data-testid={`toggle-flag-emp-${f.employee_id}`}
                        >
                          {isDismissed ? <><RotateCcw className="h-3 w-3 mr-1" />Restore</> : 'Dismiss'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Project flags */}
            {projFlags.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">Projects &mdash; No Status Update in 7+ Days</p>
                <div className="space-y-1">
                  {projFlags.map(f => {
                    const isDismissed = dismissedProjs.has(f.project_id);
                    return (
                      <div key={f.project_id} className={`flex items-center justify-between py-2 px-3 rounded-md border ${isDismissed ? 'border-border/50 bg-secondary/10 opacity-60' : 'border-amber-500/20 bg-amber-500/5'}`} data-testid={`flag-proj-${f.project_id}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <AlertCircle className={`h-3.5 w-3.5 shrink-0 ${isDismissed ? 'text-muted-foreground' : 'text-amber-400'}`} />
                          <span className={`text-sm font-medium truncate ${isDismissed ? 'line-through text-muted-foreground' : ''}`}>{f.project_number} - {f.project_name}</span>
                          <Badge variant="outline" className="text-xs shrink-0">{f.status}</Badge>
                          <span className="text-xs text-rose-400 font-mono shrink-0">{f.days_since_update}d</span>
                          {f.already_alerted && <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-500/30 shrink-0"><Check className="h-2.5 w-2.5 mr-0.5" />Alerted</Badge>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-muted-foreground hover:text-foreground shrink-0 ml-2"
                          onClick={() => isDismissed ? restoreFlag('project', f.project_id) : dismissFlag('project', f.project_id)}
                          data-testid={`toggle-flag-proj-${f.project_id}`}
                        >
                          {isDismissed ? <><RotateCcw className="h-3 w-3 mr-1" />Restore</> : 'Dismiss'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {totalFlags === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">No delinquency flags for this period.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Aging */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" /> Invoice Aging Report
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Aging Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {agingBuckets.map(b => (
              <Card key={b.key} className={`border-border shadow-none cursor-pointer transition-colors ${agingFilter === b.key ? 'bg-primary/15 border-primary/40' : 'bg-secondary/20 hover:bg-secondary/40'}`} onClick={() => setAgingFilter(agingFilter === b.key ? null : b.key)} data-testid={`aging-card-${b.key}`}>
                <CardContent className="p-3 text-center">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{b.label}</p>
                  <p className={`text-lg font-bold mt-1 tabular-nums ${b.color}`}>{fmt(aging?.totals?.[b.key] || 0)}</p>
                  <p className="text-xs text-muted-foreground">{(aging?.buckets?.[b.key] || []).length} invoices</p>
                </CardContent>
              </Card>
            ))}
            <Card className={`border-border shadow-none cursor-pointer transition-colors ${agingFilter === null ? '' : 'hover:bg-secondary/40'}`} onClick={() => setAgingFilter(null)}>
              <CardContent className="p-3 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Outstanding</p>
                <p className="text-lg font-bold mt-1 tabular-nums text-primary">{fmt(aging?.totals?.total || 0)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Active filter indicator */}
          {agingFilter && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{agingBuckets.find(b => b.key === agingFilter)?.label || agingFilter}</Badge>
              <Button variant="ghost" size="sm" className="text-xs h-5 text-muted-foreground" onClick={() => setAgingFilter(null)}>Clear</Button>
            </div>
          )}

          {/* Aging Detail Table */}
          {(() => {
            let allAging = [
              ...(aging?.buckets?.current || []).map(e => ({ ...e, bucket: 'Current', bucketKey: 'current' })),
              ...(aging?.buckets?.['30'] || []).map(e => ({ ...e, bucket: '31-60', bucketKey: '30' })),
              ...(aging?.buckets?.['60'] || []).map(e => ({ ...e, bucket: '61-90', bucketKey: '60' })),
              ...(aging?.buckets?.['90_plus'] || []).map(e => ({ ...e, bucket: '90+', bucketKey: '90_plus' })),
            ];
            // Apply aging filter
            if (agingFilter) {
              allAging = allAging.filter(e => e.bucketKey === agingFilter);
            }
            if (allAging.length === 0) return <p className="text-xs text-muted-foreground text-center py-3">{agingFilter ? 'No invoices in this aging period' : 'No outstanding invoices'}</p>;
            return (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Client</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-center">Days</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-center">Bucket</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allAging.map(e => (
                    <TableRow key={e.id} className="border-border">
                      <TableCell className="text-xs font-mono">{e.invoice_number}</TableCell>
                      <TableCell className="text-xs">{e.client_name || '-'}</TableCell>
                      <TableCell className="text-xs">{e.project_name || '-'}</TableCell>
                      <TableCell className="text-xs text-center tabular-nums">{e.days_outstanding}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${e.bucket === '90+' ? 'text-rose-400 border-rose-500/30' : e.bucket === '61-90' ? 'text-orange-400 border-orange-500/30' : e.bucket === '31-60' ? 'text-amber-400 border-amber-500/30' : 'text-emerald-400 border-emerald-500/30'}`}>{e.bucket}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-medium">{fmt(e.balance_due)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* AP Aging */}
      <Card className="border-border bg-card shadow-none" data-testid="ap-aging-report">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" /> Accounts Payable Aging
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* AP Aging Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {agingBuckets.map(b => (
              <Card key={`ap-${b.key}`} className={`border-border shadow-none cursor-pointer transition-colors ${apAgingFilter === b.key ? 'bg-primary/15 border-primary/40' : 'bg-secondary/20 hover:bg-secondary/40'}`} onClick={() => setApAgingFilter(apAgingFilter === b.key ? null : b.key)} data-testid={`ap-aging-card-${b.key}`}>
                <CardContent className="p-3 text-center">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{b.label}</p>
                  <p className={`text-lg font-bold mt-1 tabular-nums ${b.color}`}>{fmt(apAging?.totals?.[b.key] || 0)}</p>
                  <p className="text-xs text-muted-foreground">{(apAging?.buckets?.[b.key] || []).length} bills</p>
                </CardContent>
              </Card>
            ))}
            <Card className={`border-border shadow-none cursor-pointer transition-colors ${apAgingFilter === null ? '' : 'hover:bg-secondary/40'}`} onClick={() => setApAgingFilter(null)}>
              <CardContent className="p-3 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Outstanding</p>
                <p className="text-lg font-bold mt-1 tabular-nums text-primary">{fmt(apAging?.totals?.total || 0)}</p>
              </CardContent>
            </Card>
          </div>

          {apAgingFilter && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{agingBuckets.find(b => b.key === apAgingFilter)?.label || apAgingFilter}</Badge>
              <Button variant="ghost" size="sm" className="text-xs h-5 text-muted-foreground" onClick={() => setApAgingFilter(null)}>Clear</Button>
            </div>
          )}

          {(() => {
            let allApAging = [
              ...(apAging?.buckets?.current || []).map(e => ({ ...e, bucket: 'Current', bucketKey: 'current' })),
              ...(apAging?.buckets?.['30'] || []).map(e => ({ ...e, bucket: '31-60', bucketKey: '30' })),
              ...(apAging?.buckets?.['60'] || []).map(e => ({ ...e, bucket: '61-90', bucketKey: '60' })),
              ...(apAging?.buckets?.['90_plus'] || []).map(e => ({ ...e, bucket: '90+', bucketKey: '90_plus' })),
            ];
            if (apAgingFilter) allApAging = allApAging.filter(e => e.bucketKey === apAgingFilter);
            if (allApAging.length === 0) return <p className="text-xs text-muted-foreground text-center py-3">{apAgingFilter ? 'No AP bills in this aging period' : 'No outstanding payables'}</p>;
            return (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider">Vendor</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Invoice #</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Category</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-center">Days</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-center">Bucket</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allApAging.map(e => (
                    <TableRow key={e.id} className="border-border">
                      <TableCell className="text-xs font-medium">{e.vendor_name}</TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">{e.invoice_number || '-'}</TableCell>
                      <TableCell className="text-xs"><Badge variant="outline" className="text-xs">{e.category}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-[120px]">{e.project_name || '-'}</TableCell>
                      <TableCell className="text-xs text-center tabular-nums">{e.days_outstanding}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={`text-xs ${e.bucket === '90+' ? 'text-rose-400 border-rose-500/30' : e.bucket === '61-90' ? 'text-orange-400 border-orange-500/30' : e.bucket === '31-60' ? 'text-amber-400 border-amber-500/30' : 'text-emerald-400 border-emerald-500/30'}`}>{e.bucket}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-medium">{fmt(e.balance_due)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </CardContent>
      </Card>

      {/* Monthly Billing Summary */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading">Monthly Billing Summary</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">How much of our billed work has been collected. Grouped by the month invoices were sent.</p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Billing Month</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Invoices</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Total Invoiced</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Total Collected</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Outstanding</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Collection %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billingMonths.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-xs py-6">No billing data.</TableCell></TableRow>
              ) : billingMonths.map(m => {
                const d = billingByMonth[m];
                const rawPct = d.invoiced > 0 ? (d.collected / d.invoiced) * 100 : 0;
                const pct = d.outstanding > 0 && rawPct > 99 ? Math.floor(rawPct) : Math.round(rawPct);
                const isExpanded = revenueDetailMonth === m;
                return (
                  <React.Fragment key={m}>
                    <TableRow className={`border-border cursor-pointer hover:bg-secondary/30 transition-colors ${isExpanded ? 'bg-primary/5' : ''}`} onClick={() => setRevenueDetailMonth(isExpanded ? null : m)} data-testid={`billing-row-${m}`}>
                      <TableCell className="text-xs font-medium flex items-center gap-1">
                        <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        {m}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{d.count}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmt(d.invoiced)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-emerald-400">{fmt(d.collected)}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-amber-400">{d.outstanding > 0 ? fmt(d.outstanding) : '-'}</TableCell>
                      <TableCell className="text-xs text-right">
                        <span className={Number(pct) >= 80 ? 'text-emerald-400' : Number(pct) >= 50 ? 'text-amber-400' : 'text-rose-400'}>
                          {pct}%
                        </span>
                      </TableCell>
                    </TableRow>
                    {isExpanded && d.invoices.map(inv => (
                      <TableRow key={inv.id} className="border-border bg-secondary/10">
                        <TableCell className="text-xs font-mono pl-8">{inv.invoice_number}</TableCell>
                        <TableCell className="text-xs">{inv.client_name || inv.project_name || '-'}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{fmt(inv.total)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-emerald-400">{fmt(inv.amount_paid || 0)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-amber-400">{(inv.balance_due || 0) > 0 ? fmt(inv.balance_due) : '-'}</TableCell>
                        <TableCell className="text-xs text-right">
                          <Badge variant="outline" className={`text-xs ${inv.status === 'Paid' ? 'text-emerald-400 border-emerald-500/30' : inv.status === 'Partial' ? 'text-amber-400 border-amber-500/30' : inv.status === 'Cancelled' ? 'text-red-400 border-red-500/30' : 'text-primary border-primary/30'}`}>{inv.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {billingMonths.length > 0 && (
                <TableRow className="border-border bg-secondary/20 font-medium">
                  <TableCell className="text-xs font-semibold">Total</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{billingMonths.reduce((s, m) => s + billingByMonth[m].count, 0)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold">{fmt(billingMonths.reduce((s, m) => s + billingByMonth[m].invoiced, 0))}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold text-emerald-400">{fmt(billingMonths.reduce((s, m) => s + billingByMonth[m].collected, 0))}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold text-amber-400">{fmt(billingMonths.reduce((s, m) => s + billingByMonth[m].outstanding, 0))}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Monthly Cash Receipts */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-heading">Monthly Cash Receipts</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Actual cash received each month based on payment dates. Shows when money hit the bank.</p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Month</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Payments</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Total Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cashMonths.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground text-xs py-6">No payments received.</TableCell></TableRow>
              ) : cashMonths.map(m => {
                const d = cashByMonth[m];
                const isExpanded = cashDetailMonth === m;
                return (
                  <React.Fragment key={m}>
                    <TableRow className={`border-border cursor-pointer hover:bg-secondary/30 transition-colors ${isExpanded ? 'bg-primary/5' : ''}`} onClick={() => setCashDetailMonth(isExpanded ? null : m)} data-testid={`cash-row-${m}`}>
                      <TableCell className="text-xs font-medium flex items-center gap-1">
                        <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        {m}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{d.paymentCount}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-emerald-400 font-medium">{fmt(d.received)}</TableCell>
                    </TableRow>
                    {isExpanded && d.payments.sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((p, idx) => (
                      <TableRow key={`${p.invoice_id}-${idx}`} className="border-border bg-secondary/10">
                        <TableCell className="text-xs pl-8">
                          <span className="font-mono text-muted-foreground">{p.date}</span>
                          <span className="ml-2">{p.client_name}</span>
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground">{p.invoice_number}{p.method ? ` / ${p.method}` : ''}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-emerald-400">{fmt(p.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {cashMonths.length > 0 && (
                <TableRow className="border-border bg-secondary/20 font-medium">
                  <TableCell className="text-xs font-semibold">Total</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{cashMonths.reduce((s, m) => s + cashByMonth[m].paymentCount, 0)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold text-emerald-400">{fmt(cashMonths.reduce((s, m) => s + cashByMonth[m].received, 0))}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Revenue Concentration (Pareto / 80-20) - customizable by group + metric + date range */}
      <RevenueConcentrationCard />
    </div>
  );
}
